import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import { Table } from 'primeng/table';
import {Router} from '@angular/router';
import {ConfiguratorTemplateHtmlService, DatastoreDto, Pageable, Partner, TemplateHtmlDTO} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, LazyLoadEvent, MessageService, MenuItem} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';

@Component({
    templateUrl: './bloc-html.component.html',
    styleUrls: ['./bloc-html.component.scss'],
    standalone: false
})
export class BlocHtmlComponent implements OnInit {
    breadcrumb = [
        {label: this.translate.instant('menu.marketing.label')},
        {label: this.translate.instant('menu.workflow.html-blocks')}
    ];
    description = this.translate.instant('blocHtml.list.description');

    datastore: DatastoreDto;
    partner: Partner;

    templates: TemplateHtmlDTO[] = [];
    totalRecords: number;

    actualEvent: LazyLoadEvent = {
        first: 0,
        rows: 25
    };

    state: { message: string };

    // Export/Import
    selectedTemplates: TemplateHtmlDTO[] = [];
    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];

    @ViewChild('templateFileInput') templateFileInput: ElementRef;

    constructor(protected templateHtmlService: ConfiguratorTemplateHtmlService, private confirmationService: ConfirmationService,
                private messageService: MessageService, private router: Router, private readonly translate: TranslateService) {
        const navigation = this.router.getCurrentNavigation();
        const state = navigation?.extras?.state as { message: string } | undefined;
        this.state = state;
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.marketing.label')},
            {label: this.translate.instant('menu.workflow.html-blocks')}
        ];
        this.description = this.translate.instant('blocHtml.list.description');
    }

    ngOnInit(): void {
        this.initExportMenu();

        if (this.state?.message === 'success_created') {
            setTimeout(() => this.messageService.add({severity: 'success', summary: this.translate.instant('datastore.successSummary'), detail: this.translate.instant('blocHtml.list.createdDetail')}));
        } else if (this.state?.message === 'success_updated') {
            setTimeout(() => this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('datastore.successSummary'),
                detail: this.translate.instant('blocHtml.list.updatedDetail')
            }));
        }
    }

    @ViewChild(Table) table: Table;
    search = '';

    onSearch(value: string) {
        this.search = value;
        this.table ? this.table.reset() : this.loadLazy();
    }

    loadLazy(event: LazyLoadEvent = null) {
        if (event) {
            this.actualEvent = event;
        }
        const start = Math.round(this.actualEvent.first / this.actualEvent.rows);
        const end = this.actualEvent.rows;
        if (this.datastore){
            if(this.partner){
                const pageable : Pageable = {
                    page: start,
                    size: end,
                    sort: []
                }
                this.templateHtmlService.getAllTemplateHtmlsByPartner(this.datastore.code, this.partner.code, pageable, this.search, 'response').subscribe((res) => {
                    this.totalRecords = +res.headers.get('x-total-count');
                    this.templates = res.body;
                    this.updateExportMenuState();
                });
            }
            else {
                this.templateHtmlService.getAllTemplateHtmlsUsingGET(this.datastore.code, start, end, [], this.search, 'response').subscribe((res) => {
                    this.totalRecords = +res.headers.get('x-total-count');
                    this.templates = res.body;
                    this.updateExportMenuState();
                });
            }
        }
    }

    remove(template: TemplateHtmlDTO) {
        this.confirmationService.confirm({
            message: this.translate.instant('blocHtml.list.confirmDeleteMessage', {name: template.name}),
            accept: () => {
                this.templateHtmlService.deleteTemplateHtmlUsingDELETE(this.datastore.code, template.id).subscribe(() => {
                    this.loadLazy();
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('blocHtml.list.deleteSuccessDetail')
                    });
                }, () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('landingPage.template.genericErrorDetail')
                    });
                });
            }
        });
    }

    // ============ EXPORT/IMPORT METHODS ============

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedTemplates(),
                disabled: !this.selectedTemplates || this.selectedTemplates.length === 0
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fas fa-download',
                command: () => this.exportAllTemplates(),
                disabled: !this.templates || this.templates.length === 0
            }
        ];
    }

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.templateFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('datastore.errorSummary'),
                detail: this.translate.instant('subscriptions.unsupportedFormatDetail')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.templateHtmlService.importTemplateHtmlJson(this.datastore.code, jsonContent).subscribe(
                (entity: TemplateHtmlDTO) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('blocHtml.list.importSuccessDetail', {name: entity.name})
                    });
                    this.loadLazy();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.templateHtmlService.importTemplateHtmlsZip(this.datastore.code, file).subscribe(
            (entities: TemplateHtmlDTO[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('blocHtml.list.importSuccessMultipleDetail', {count: entities.length})
                });
                this.loadLazy();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte un seul template en JSON
     */
    exportTemplateJson(template: TemplateHtmlDTO) {
        this.templateHtmlService.exportTemplateHtmlJson(this.datastore.code, template.id).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `template-html-${template.code}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('blocHtml.list.exportSuccessDetail')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte plusieurs templates en ZIP
     */
    exportSelectedTemplates() {
        if (!this.selectedTemplates || this.selectedTemplates.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('blocHtml.list.noTemplateSelectedDetail')
            });
            return;
        }

        const ids = this.selectedTemplates.map(c => c.id);
        this.templateHtmlService.exportTemplateHtmlsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `templates-html-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('blocHtml.list.exportSuccessMultipleDetail', {count: this.selectedTemplates.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte tous les templates filtrés en ZIP
     */
    exportAllTemplates() {
        if (!this.templates || this.templates.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('blocHtml.list.noTemplateToExportDetail')
            });
            return;
        }

        const ids = this.templates.map(c => c.id);
        this.templateHtmlService.exportTemplateHtmlsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `templates-html-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('blocHtml.list.exportSuccessMultipleDetail', {count: this.templates.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedTemplates || this.selectedTemplates.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = !this.templates || this.templates.length === 0;
            }
        }
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }
}
